/**
 * Sons leves do piso (Web Audio). Sem arquivo, sem trilha Twilio.
 * Só toca depois de um gesto e com a aba visível.
 */

export const VO_FLOOR_TONES = {
  welcome: [{ freq: 494, duration: 0.12, gain: 0.045, wave: "sine" }],
  arrive: [
    { freq: 523, duration: 0.1, gain: 0.05, wave: "sine" },
    { freq: 659, start: 0.09, duration: 0.16, gain: 0.055, wave: "sine" },
  ],
  leave: [
    { freq: 392, duration: 0.1, gain: 0.045, wave: "sine" },
    { freq: 330, start: 0.1, duration: 0.12, gain: 0.04, wave: "sine" },
    { freq: 262, start: 0.22, duration: 0.18, gain: 0.038, wave: "sine" },
  ],
  react_wave: [
    { freq: 587, duration: 0.08, gain: 0.05, wave: "sine" },
    { freq: 740, start: 0.08, duration: 0.12, gain: 0.048, wave: "sine" },
  ],
  react_coffee: [
    { freq: 349, duration: 0.07, gain: 0.042, wave: "triangle" },
    { freq: 523, start: 0.06, duration: 0.1, gain: 0.038, wave: "sine" },
  ],
  react_clap: [
    { freq: 220, duration: 0.05, gain: 0.05, wave: "triangle" },
    { freq: 196, start: 0.07, duration: 0.05, gain: 0.042, wave: "triangle" },
  ],
  react_party: [
    { freq: 523, duration: 0.07, gain: 0.045, wave: "sine" },
    { freq: 659, start: 0.07, duration: 0.07, gain: 0.048, wave: "sine" },
    { freq: 784, start: 0.14, duration: 0.14, gain: 0.05, wave: "sine" },
  ],
  react_default: [{ freq: 659, duration: 0.1, gain: 0.045, wave: "sine" }],
  prompt: [
    { freq: 440, duration: 0.07, gain: 0.04, wave: "triangle" },
    { freq: 554, start: 0.08, duration: 0.1, gain: 0.038, wave: "sine" },
  ],
  pulse: [{ freq: 698, duration: 0.14, gain: 0.042, wave: "sine" }],
  kudos: [
    { freq: 523, duration: 0.08, gain: 0.05, wave: "sine" },
    { freq: 659, start: 0.08, duration: 0.08, gain: 0.05, wave: "sine" },
    { freq: 784, start: 0.16, duration: 0.08, gain: 0.052, wave: "sine" },
    { freq: 1046, start: 0.24, duration: 0.16, gain: 0.046, wave: "sine" },
  ],
  spark: [
    { freq: 392, duration: 0.09, gain: 0.05, wave: "triangle" },
    { freq: 523, start: 0.1, duration: 0.1, gain: 0.052, wave: "sine" },
    { freq: 659, start: 0.2, duration: 0.14, gain: 0.05, wave: "sine" },
    { freq: 784, start: 0.32, duration: 0.18, gain: 0.042, wave: "sine" },
  ],
  chat: [
    { freq: 784, duration: 0.07, gain: 0.04, wave: "sine" },
    { freq: 988, start: 0.06, duration: 0.1, gain: 0.038, wave: "sine" },
  ],
  chat_mention: [
    { freq: 659, duration: 0.09, gain: 0.06, wave: "sine" },
    { freq: 880, start: 0.1, duration: 0.11, gain: 0.068, wave: "triangle" },
    { freq: 1174, start: 0.22, duration: 0.16, gain: 0.06, wave: "sine" },
  ],
};

const REACTION_KIND = {
  "👋": "react_wave",
  "☕": "react_coffee",
  "👏": "react_clap",
  "🎉": "react_party",
  "🏆": "kudos",
};

let sharedCtx = null;
let unlocked = false;
let queue = Promise.resolve();
let speakerMuted = false;

export function setVoFloorSpeakerMuted(muted) {
  speakerMuted = Boolean(muted);
}

export function isVoFloorSpeakerMuted() {
  return speakerMuted;
}

function getAudioContext() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!sharedCtx || sharedCtx.state === "closed") sharedCtx = new Ctx();
    return sharedCtx;
  } catch {
    return null;
  }
}

export function unlockVoFloorAudio() {
  unlocked = true;
  const ctx = getAudioContext();
  if (ctx && ctx.state === "suspended") {
    void ctx.resume().catch(() => {});
  }
}

export function isVoFloorAudioUnlocked() {
  return unlocked;
}

export function soundKindForReaction(emoji) {
  return REACTION_KIND[String(emoji || "")] || "react_default";
}

export function canPlayVoFloorSound({ hidden, unlocked: ready, speakerMuted: mutedFlag } = {}) {
  if ((mutedFlag ?? speakerMuted) === true) return false;
  if (ready === false) return false;
  if (hidden) return false;
  return true;
}

/** Chat toca mesmo com a aba em segundo plano — é o alerta. */
export function canPlayVoChatSound({ unlocked: ready, speakerMuted: mutedFlag } = {}) {
  if ((mutedFlag ?? speakerMuted) === true) return false;
  if (ready === false) return false;
  return true;
}

async function ensureRunning() {
  const ctx = getAudioContext();
  if (!ctx) return false;
  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch {
      return false;
    }
  }
  if (ctx.state === "running") {
    unlocked = true;
    return true;
  }
  return false;
}

async function playToneSequence(steps) {
  if (speakerMuted) return false;
  if (typeof document !== "undefined" && document.hidden) return false;
  const ready = await ensureRunning();
  if (!ready) return false;
  const ctx = getAudioContext();
  if (!ctx) return false;
  for (const step of steps) {
    const { freq, start = 0, duration = 0.1, gain = 0.045, wave = "sine" } = step;
    if (!freq || duration <= 0) continue;
    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    osc.type = wave;
    osc.frequency.value = freq;
    const t0 = ctx.currentTime + start;
    const attack = Math.min(0.012, duration * 0.25);
    gainNode.gain.setValueAtTime(0.0001, t0);
    gainNode.gain.linearRampToValueAtTime(gain, t0 + attack);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    osc.connect(gainNode);
    gainNode.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + duration + 0.03);
  }
  return true;
}

export function playVoFloorSound(kind) {
  const steps = VO_FLOOR_TONES[kind] || VO_FLOOR_TONES.react_default;
  queue = queue.then(() => playToneSequence(steps)).catch(() => false);
  return queue;
}
